import { gql } from '@apollo/client';
import client from '../apollo/client';

// GraphQL queries for secure backend communication
const GET_BANKS_QUERY = gql`
  query GetBanks($currency: String, $country: String, $type: String) {
    getBanks(currency: $currency, country: $country, type: $type) {
      code
      name
      longcode
      active
    }
  }
`;

// Bank name mappings for better UI display
const BANK_NAME_MAPPINGS = {
    'Guaranty Trust Bank': 'GT Bank',
    'United Bank For Africa': 'UBA',
    'First City Monument Bank': 'FCMB',
    'Access Bank': 'Access Bank',
    'Zenith Bank': 'Zenith Bank',
    'First Bank of Nigeria': 'First Bank',
    'Union Bank of Nigeria': 'Union Bank',
    'Sterling Bank': 'Sterling Bank',
    'Stanbic IBTC Bank': 'Stanbic IBTC',
    'Fidelity Bank': 'Fidelity Bank',
    'Unity Bank': 'Unity Bank',
    'Keystone Bank': 'Keystone Bank',
    'Wema Bank': 'Wema Bank',
    'Polaris Bank': 'Polaris Bank',
    'Jaiz Bank': 'Jaiz Bank',
    'Kuda Bank': 'Kuda Bank',
    'Opay': 'Opay',
    'PalmPay': 'PalmPay',
    'Moniepoint': 'Moniepoint',
    'VFD Microfinance Bank': 'VFD Bank'
};

/**
 * Fetches the list of banks from backend via GraphQL
 * @returns {Promise<Array>} Array of bank objects with name and code
 */
export const fetchBanks = async () => {
    try {
        const { data } = await client.query({
            query: GET_BANKS_QUERY,
            variables: {
                country: 'nigeria',
                currency: 'NGN'
            },
            fetchPolicy: 'cache-first',
            errorPolicy: 'all'
        });

        if (!data?.getBanks) {
            throw new Error('No banks data received from server');
        }

        // Filter active Nigerian banks and apply custom display names
        return data.getBanks
            .filter(bank => bank.active && bank.name)
            .sort((a, b) => {
                const nameA = BANK_NAME_MAPPINGS[a.name] || a.name;
                const nameB = BANK_NAME_MAPPINGS[b.name] || b.name;
                return nameA.localeCompare(nameB);
            })
            .map(bank => ({
                name: BANK_NAME_MAPPINGS[bank.name] || bank.name,
                code: bank.code,
                longcode: bank.longcode,
                originalName: bank.name
            }));

    } catch (error) {
        console.error('Error fetching banks:', error);

        // Return a fallback list of major Nigerian banks if API fails
        const fallbackBanks = [
            { name: 'GT Bank', code: '058', originalName: 'Guaranty Trust Bank' },
            { name: 'Access Bank', code: '044', originalName: 'Access Bank' },
            { name: 'Zenith Bank', code: '057', originalName: 'Zenith Bank' },
            { name: 'First Bank', code: '011', originalName: 'First Bank of Nigeria' },
            { name: 'UBA', code: '033', originalName: 'United Bank For Africa' },
            { name: 'Fidelity Bank', code: '070', originalName: 'Fidelity Bank' },
            { name: 'Sterling Bank', code: '232', originalName: 'Sterling Bank' },
            { name: 'Stanbic IBTC', code: '221', originalName: 'Stanbic IBTC Bank' },
            { name: 'Union Bank', code: '032', originalName: 'Union Bank of Nigeria' },
            { name: 'Kuda Bank', code: '50211', originalName: 'Kuda Bank' }
        ];

        console.warn('Using fallback bank list due to API error');
        return fallbackBanks;
    }
};

/**
 * Validates bank account details before submission
 * @param {Object} accountDetails - Account details to validate
 * @returns {Object} Validation result
 */
export const validateBankAccountDetails = (accountDetails) => {
    const errors = [];

    if (!accountDetails.bankName?.trim()) {
        errors.push('Bank name is required');
    }

    if (!accountDetails.accountNumber?.trim()) {
        errors.push('Account number is required');
    } else if (!/^\d{10}$/.test(accountDetails.accountNumber.trim())) {
        errors.push('Account number must be exactly 10 digits');
    }

    if (!accountDetails.bankCode?.trim()) {
        errors.push('Bank code is required');
    }

    if (!accountDetails.accountName?.trim()) {
        errors.push('Account name is required');
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};

/**
 * Formats bank account details for display
 * @param {Object} accountDetails - Raw account details
 * @returns {Object} Formatted account details
 */
export const formatBankAccountDetails = (accountDetails) => {
    if (!accountDetails) return null;

    return {
        ...accountDetails,
        accountNumber: accountDetails.accountNumber?.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3'),
        accountName: accountDetails.accountName?.toUpperCase(),
        bankName: BANK_NAME_MAPPINGS[accountDetails.bankName] || accountDetails.bankName
    };
};

/**
 * Checks if bank account details are complete
 * @param {Object} accountDetails - Account details to check
 * @returns {boolean} Whether details are complete
 */
export const isBankAccountComplete = (accountDetails) => {
    return !!(
        accountDetails?.bankName &&
        accountDetails?.accountNumber &&
        accountDetails?.accountName &&
        accountDetails?.bankCode
    );
};

/**
 * Gets bank name from bank code
 * @param {string} bankCode - Bank code to lookup
 * @param {Array} banks - Array of bank objects
 * @returns {string} Bank name or 'Unknown Bank'
 */
export const getBankNameFromCode = (bankCode, banks = []) => {
    const bank = banks.find(b => b.code === bankCode);
    return bank ? (BANK_NAME_MAPPINGS[bank.originalName] || bank.name) : 'Unknown Bank';
};

/**
 * Sanitizes bank account data before sending to backend
 * @param {Object} accountDetails - Raw account details
 * @returns {Object} Sanitized account details
 */
export const sanitizeBankAccountDetails = (accountDetails) => {
    return {
        bankName: accountDetails.bankName?.trim(),
        accountNumber: accountDetails.accountNumber?.replace(/\D/g, ''),
        accountName: accountDetails.accountName?.trim(),
        bankCode: accountDetails.bankCode?.trim()
    };
};

// Export additional utility functions
export const paystackUtils = {
    fetchBanks,
    validateBankAccountDetails,
    formatBankAccountDetails,
    isBankAccountComplete,
    getBankNameFromCode,
    sanitizeBankAccountDetails,
    BANK_NAME_MAPPINGS
};

export default paystackUtils;